import { useCallback, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { AsyncSelect } from "@/components/ui/v2/async-select";
import { FormLabel } from "@/components/ui/v2/form";
import { Label } from "@/components/ui/v2/label";
import { integrationsApi } from "@/features/integrations/api/integrations.api";

export type IntegrationModuleOption = {
  value: string;
  label: string;
  /** Stable integration key from the API (e.g. "indeed"), for branching — not the translated label. */
  integrationName: string;
};

export interface IntegrationModuleSelectProps {
  fieldName?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  disabledOptions?: string[];
  clientId?: string;
  useFormLabel?: boolean;
  /** Module name to filter by (e.g. "campaigns" for campaign job boards). Required. */
  moduleFilter: string;
  /** When true, display integration name instead of module name (e.g. "Adzuna" vs "Campaigns"). */
  displayAsIntegrationName?: boolean;
  onIntegrationNameChange?: (name: string | undefined) => void;
  /** Emits the stable integration key (e.g. "indeed") for branching, distinct from the translated label. */
  onIntegrationKeyChange?: (key: string | undefined) => void;
}

export function IntegrationModuleSelect({
  fieldName = "clientIntegrationModuleId",
  label = "Integration Module",
  placeholder = "Select an integration module",
  disabled,
  disabledOptions,
  clientId,
  useFormLabel = false,
  moduleFilter,
  displayAsIntegrationName = false,
  onIntegrationNameChange,
  onIntegrationKeyChange,
}: IntegrationModuleSelectProps) {
  const { control, setValue } = useFormContext();
  const { t, i18n } = useTranslation();

  const selectedValue = useWatch({ control, name: fieldName }) as
    | string
    | undefined;

  const [optionsCache, setOptionsCache] = useState<IntegrationModuleOption[]>([]);

  const isOptionDisabled = (optionValue: string) => {
    return disabledOptions?.includes(optionValue) ?? false;
  };

  const fetcher = useCallback(
    async (query?: string) => {
      try {
        const params = { clientId: clientId || undefined };
        const items = await integrationsApi.fetchIntegrationModulesForModule(moduleFilter, params);

        const searchLower = (query ?? "").toLowerCase();
        const allOptions: IntegrationModuleOption[] = items
          .map((i) => {
            const displayLabel =
              displayAsIntegrationName && i.displayName && i18n.exists(i.displayName)
                ? t(i.displayName)
                : i.displayName || i.integrationName;
            const optionValue = i.clientIntegrationModuleId ?? i.integrationModuleId;
            return { value: optionValue, label: displayLabel, integrationName: i.integrationName };
          })
          .filter((o) => !searchLower || o.label.toLowerCase().includes(searchLower));

        setOptionsCache(allOptions);

        if (selectedValue && (onIntegrationNameChange || onIntegrationKeyChange)) {
          const match = allOptions.find((o) => o.value === selectedValue);
          onIntegrationNameChange?.(match?.label);
          onIntegrationKeyChange?.(match?.integrationName);
        }

        const selected = allOptions.find((o) => o.value === selectedValue);
        if (selected && allOptions[0]?.value !== selectedValue) {
          const rest = allOptions.filter((o) => o.value !== selectedValue);
          return [selected, ...rest];
        }
        return allOptions;
      } catch {
        return [];
      }
    },
    [moduleFilter, clientId, displayAsIntegrationName, selectedValue, t, i18n, onIntegrationNameChange, onIntegrationKeyChange]
  );

  const filterFn = useCallback(
    (option: IntegrationModuleOption, filterQuery: string) =>
      option.label.toLowerCase().includes(filterQuery.toLowerCase()),
    []
  );

  const LabelComponent = useFormLabel ? FormLabel : Label;

  return (
    <div className="space-y-2">
      {label && <LabelComponent>{label}</LabelComponent>}
      <AsyncSelect
        width={"100%"}
        fetcher={fetcher}
        preload
        filterFn={filterFn}
        value={selectedValue || ""}
        onChange={(value) => {
          setValue(fieldName, value);
          if (onIntegrationNameChange || onIntegrationKeyChange) {
            const match = optionsCache.find((o) => o.value === value);
            onIntegrationNameChange?.(match?.label);
            onIntegrationKeyChange?.(match?.integrationName);
          }
        }}
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        disabledOptions={disabledOptions}
        renderOption={(option: IntegrationModuleOption) => (
          <span className={isOptionDisabled(option.value) ? "opacity-50" : ""}>
            {option.label}
          </span>
        )}
        getOptionValue={(option: IntegrationModuleOption) => option.value}
        getDisplayValue={(option: IntegrationModuleOption) => option.label}
        noResultsMessage={t("common.noEntityFound", {
          entity: (displayAsIntegrationName
            ? t("common.jobBoards")
            : t("common.modules")
          ).toLowerCase(),
        })}
      />
    </div>
  );
}
